import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  ModalBody,
  ModalFooter,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { ModalComponent } from '../../../components/_components_0.2/Modal';
import { apllyToast } from '../../../components/Global/Toast2.0';
import {
  FuelPump,
  fuelErrorMessage,
  fuelReadingsService,
} from '../../../services/fuelReadingsService';
import { WEEKDAYS, formatDateTime } from '../../../utils/fuelReadings';

const STATUS_REASON_MIN = 5; // igual à api

interface Props {
  isOpen: boolean;
  onClose: () => void;
  pump?: FuelPump;
}

const EMPTY = {
  code: '',
  name: '',
  fuel_type: 'Diesel S10',
  branch: 'Matriz',
  erp_product_code: '8607',
  expected_times: '07:00,17:00',
  saturday_times: '07:00,11:00',
  expected_weekdays: [1, 2, 3, 4, 5, 6],
  max_jump_liters: '6000',
  active: true,
  display_order: '0',
};

// cadastro da bomba (só admin)
export function PumpModal({ isOpen, onClose, pump }: Props) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [statusReason, setStatusReason] = useState('');
  // ativar/desativar uma bomba que já existe pede o motivo (vai para o histórico)
  const statusChanged = !!pump && form.active !== pump.active;
  const history = useQuery(
    ['fuel-readings', 'pump-history', pump?.id],
    () => fuelReadingsService.pumpStatusHistory(pump?.id as string),
    { enabled: isOpen && !!pump },
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setStatusReason('');
    setForm(
      pump
        ? {
            code: pump.code,
            name: pump.name,
            fuel_type: pump.fuel_type,
            branch: pump.branch || '',
            erp_product_code: pump.erp_product_code
              ? String(pump.erp_product_code)
              : '',
            expected_times: pump.expected_times,
            saturday_times: pump.saturday_times || '',
            expected_weekdays: pump.expected_weekdays
              .split(',')
              .filter(Boolean)
              .map(Number),
            max_jump_liters: String(pump.max_jump_liters),
            active: pump.active,
            display_order: String(pump.display_order),
          }
        : EMPTY,
    );
  }, [isOpen, pump]);

  const set = (field: keyof typeof EMPTY, value: unknown) =>
    setForm(current => ({ ...current, [field]: value }));

  // campos numéricos: guarda só os dígitos
  const onlyDigits =
    (field: keyof typeof EMPTY) => (event: ChangeEvent<HTMLInputElement>) =>
      set(field, event.target.value.replace(/\D/g, ''));

  const toggleDay = (day: number) =>
    set(
      'expected_weekdays',
      form.expected_weekdays.includes(day)
        ? form.expected_weekdays.filter(item => item !== day)
        : [...form.expected_weekdays, day],
    );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (statusChanged && statusReason.trim().length < STATUS_REASON_MIN) {
      apllyToast(
        'warning',
        `Informe o motivo para ${
          form.active ? 'ativar' : 'desativar'
        } a bomba.`,
      );
      return;
    }

    setSaving(true);
    try {
      await fuelReadingsService.savePump(
        {
          code: form.code.trim(),
          name: form.name.trim(),
          fuel_type: form.fuel_type.trim(),
          branch: form.branch.trim() || null,
          erp_product_code: form.erp_product_code
            ? Number(form.erp_product_code)
            : null,
          expected_times: form.expected_times,
          saturday_times: form.saturday_times,
          expected_weekdays: form.expected_weekdays.sort().join(','),
          max_jump_liters: Number(form.max_jump_liters),
          active: form.active,
          display_order: Number(form.display_order) || 0,
          ...(statusChanged ? { status_reason: statusReason.trim() } : {}),
        },
        pump?.id,
      );
      await queryClient.invalidateQueries('fuel-readings');
      apllyToast('success', 'Bomba salva.');
      onClose();
    } catch (err) {
      apllyToast(
        'error',
        fuelErrorMessage(err, 'Não foi possível salvar a bomba.'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalComponent
      title={pump ? 'Editar bomba' : 'Nova bomba'}
      isOpen={isOpen}
      onClose={onClose}
      sizeSpecifyc="xl"
      scrollBehavior="inside"
      closeOnOverlayClick={false}
    >
      <Box
        as="form"
        onSubmit={handleSubmit}
        display="flex"
        flexDirection="column"
        flex="1"
        minH="0"
      >
        <ModalBody>
          <Stack spacing="4">
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing="4">
              <FormControl isRequired>
                <FormLabel htmlFor="pump-code">Código</FormLabel>
                <Input
                  id="pump-code"
                  value={form.code}
                  onChange={e => set('code', e.target.value)}
                  maxLength={20}
                />
              </FormControl>
              <FormControl isRequired gridColumn={{ md: 'span 2' }}>
                <FormLabel htmlFor="pump-name">Nome</FormLabel>
                <Input
                  id="pump-name"
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  maxLength={80}
                />
              </FormControl>
            </SimpleGrid>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing="4">
              <FormControl>
                <FormLabel htmlFor="pump-fuel">Combustível</FormLabel>
                <Input
                  id="pump-fuel"
                  value={form.fuel_type}
                  onChange={e => set('fuel_type', e.target.value)}
                  maxLength={40}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="pump-branch">Filial</FormLabel>
                <Input
                  id="pump-branch"
                  value={form.branch}
                  onChange={e => set('branch', e.target.value)}
                  maxLength={80}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="pump-erp">Produto no GesCooper</FormLabel>
                <Input
                  id="pump-erp"
                  value={form.erp_product_code}
                  onChange={onlyDigits('erp_product_code')}
                />
                <FormHelperText>8607 = Diesel B S10 aditivado</FormHelperText>
              </FormControl>
            </SimpleGrid>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
              <FormControl>
                <FormLabel htmlFor="pump-times">
                  Horários das leituras
                </FormLabel>
                <Input
                  id="pump-times"
                  value={form.expected_times}
                  onChange={e => set('expected_times', e.target.value)}
                  placeholder="07:00,17:00"
                />
                <FormHelperText>
                  Abertura e fechamento, separados por vírgula.
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="pump-saturday">
                  Horários de sábado
                </FormLabel>
                <Input
                  id="pump-saturday"
                  value={form.saturday_times}
                  onChange={e => set('saturday_times', e.target.value)}
                  placeholder="07:00,11:00"
                />
                <FormHelperText>
                  O posto fecha mais cedo. Vazio = mesmos horários.
                </FormHelperText>
              </FormControl>
            </SimpleGrid>
            <FormControl as="fieldset">
              <FormLabel as="legend">Dias com leitura</FormLabel>
              <Flex gridGap="3" wrap="wrap">
                {WEEKDAYS.map((label, day) => (
                  <Checkbox
                    key={label}
                    isChecked={form.expected_weekdays.includes(day)}
                    onChange={() => toggleDay(day)}
                  >
                    {label}
                  </Checkbox>
                ))}
              </Flex>
            </FormControl>
            <SimpleGrid
              columns={{ base: 1, md: 3 }}
              spacing="4"
              alignItems="end"
            >
              <FormControl>
                <FormLabel htmlFor="pump-jump">
                  Limite por leitura (L)
                </FormLabel>
                <Input
                  id="pump-jump"
                  value={form.max_jump_liters}
                  onChange={onlyDigits('max_jump_liters')}
                />
                <FormHelperText>
                  Acima disso o frentista confirma.
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="pump-order">Ordem</FormLabel>
                <Input
                  id="pump-order"
                  value={form.display_order}
                  onChange={onlyDigits('display_order')}
                />
              </FormControl>
              <FormControl display="flex" alignItems="center" pb="2">
                <Switch
                  id="pump-active"
                  isChecked={form.active}
                  onChange={e => set('active', e.target.checked)}
                  mr="3"
                />
                <FormLabel htmlFor="pump-active" mb="0">
                  {form.active ? 'Ativa' : 'Desativada'}
                </FormLabel>
              </FormControl>
            </SimpleGrid>
            {statusChanged && (
              <FormControl isRequired>
                <FormLabel htmlFor="pump-status-reason">
                  Motivo para {form.active ? 'ativar' : 'desativar'}
                </FormLabel>
                <Textarea
                  id="pump-status-reason"
                  value={statusReason}
                  onChange={e => setStatusReason(e.target.value)}
                  maxLength={300}
                  placeholder={
                    form.active
                      ? 'Ex.: conserto feito'
                      : 'Ex.: bomba quebrada, aguardando técnico'
                  }
                />
                <FormHelperText>
                  Fica no histórico. Os dias com a bomba desativada não contam
                  como leitura faltando.
                </FormHelperText>
              </FormControl>
            )}
            {pump && (history.data || []).length > 0 && (
              <Box>
                <Text fontWeight="bold" mb="1">
                  Histórico
                </Text>
                {(history.data || []).slice(0, 5).map(item => (
                  <Text key={item.id} fontSize="sm" color="gray.500">
                    {item.active ? 'Ativa' : 'Desativada'} ·{' '}
                    {formatDateTime(item.created_at, true)} ·{' '}
                    {item.access_name || '—'} — {item.reason}
                  </Text>
                ))}
              </Box>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter borderTopWidth="1px" borderColor="gray.600">
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button colorScheme="blue" type="submit" isLoading={saving}>
            Salvar
          </Button>
        </ModalFooter>
      </Box>
    </ModalComponent>
  );
}
